/**
 * The TWO orders a round is read in, written once.
 *
 * A round page is the same four people in two arrangements: the order they
 * teed off in, which is what the tones, the dashes and the card all run in, and
 * the order they finished in, which is what the scoreboard band and the shared
 * postcard print. Every screen that shows a round needs both.
 *
 * Both were hand-rolled twice — once on the member's round page, once on the
 * public share — with byte-identical bodies and only the player type differing.
 * These are generic over the SHAPE rather than the type for exactly that
 * reason: the member `RoundPlayer` and the public `SharedRoundPlayer` agree on
 * a seat and a gross and disagree about almost everything else, and an ordering
 * has no business knowing the rest.
 */

/** What seat order needs of a player, and nothing more. */
export interface Seated {
  /** The seat the player took in the round — 1, 2, 3, 4. */
  position: number
}

/** What finishing order needs: a seat, and the gross they signed for. */
export interface Carded extends Seated {
  /** The gross, or null while the card is unfinished. */
  total_strokes: number | null
}

/** Seat order — the order the tones, the dashes and the card all run in. */
export function seated<T extends Seated>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => a.position - b.position)
}

/**
 * Lowest gross first, and a card with no total goes LAST rather than leading
 * with a dash. Ties and unfinished cards fall back to seat order so the
 * band/postcard never reshuffles between two renders of the same round.
 *
 * A COPY, never a sort in place: `Array#sort` mutates, and these players come
 * straight off a fetched round that other sections read in their own order.
 */
export function standingOf<T extends Carded>(players: readonly T[]): T[] {
  return [...players].sort(byGrossThenSeat)
}

function byGrossThenSeat(a: Carded, b: Carded): number {
  if (a.total_strokes === null && b.total_strokes === null) return a.position - b.position
  if (a.total_strokes === null) return 1
  if (b.total_strokes === null) return -1
  if (a.total_strokes !== b.total_strokes) return a.total_strokes - b.total_strokes
  return a.position - b.position
}
